// MaterialStep.tsx (web + MUI)

import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";

import SearchIcon from "@mui/icons-material/Search";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RecyclingIcon from "@mui/icons-material/Recycling";
import DescriptionIcon from "@mui/icons-material/Description";
import FactoryIcon from "@mui/icons-material/Factory";
import TerrainIcon from "@mui/icons-material/Terrain";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useAllowScroll } from "../AllowScrollContext";
import { OrderPosition } from "@/shared/types";
import { Article, MaterialOrder, SupplierOrder } from "@/components/planning/Project";
import ApiService from "@/api/ApiService";
import { ActionItem } from "@/shared/sharedService/ActionItem";
import { ArrowRight } from "@mui/icons-material";

const formatEuro = (price) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(price);

type ArticleGroup = {
  key: string;
  name: string;
  number: string;
  plants: {
    key: string;
    name: string;
    distance: number;
    positions: OrderPosition[];
  }[];
};

type UnpricedDeliveryOption = {
  name: string;
  manualDAP: boolean;
};

function formatDistance(dist: string | number | null | undefined) {
  if (!dist || typeof dist !== "number") return "";

  return "ca. " + Math.round(dist) + " km";
}

function normalizeDeliveryTypeName(name: string | null | undefined) {
  return (name ?? "").trim().toLocaleLowerCase("de-DE").replace(/\s+/g, " ");
}

function getUnpricedDeliveryOptions(article: Article): UnpricedDeliveryOption[] {
  const standardDeliveryType = /(anlieferung|annahme|aufbruch|aushub|pechhal)/i.test(article.name) ? "Anlieferung" : "Abholung";

  return [
    {
      name: standardDeliveryType,
      manualDAP: false,
    },
    {
      name: "Frei Bau",
      manualDAP: true,
    },
  ];
}

export default function MaterialStep({
  projectId,
  order,
  isDispo,
  onBack,
  onSelect,
}: {
  projectId: number;
  isDispo: boolean;
  order: MaterialOrder;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const supplierOrderQuery = useQuery<SupplierOrder[]>({
    queryKey: ["getSupplierOrders", projectId],
    queryFn: ApiService.getSupplierOrders(projectId),
  });

  const positionsByArticleId = useMemo(() => {
    const positions: Record<number, OrderPosition[]> = {};

    if (!supplierOrderQuery.data) {
      return positions;
    }

    for (const supplierOrder of supplierOrderQuery.data) {
      for (const position of supplierOrder.positions) {
        const articleId = position.article.id;

        if (!(articleId in positions)) {
          positions[articleId] = [];
        }

        positions[articleId].push(position);
      }
    }

    return positions;
  }, [supplierOrderQuery.data]);

  const {
    setAllowScroll,
    view,
    setView,
    selectedOrderId,
    setSelectedOrderId,
    selectedPlantKey,
    setSelectedPlantKey,
    autoPlantSelected,
    setAutoPlantSelected,
  } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(view === "MATERIALS" || view === "MANUAL");
  }, [setAllowScroll, view]);

  const [searchField, setSearchField] = useState("");
  const [chosenArticle, setChosenArticle] = useState<number | null>(null);
  const [chosenPosition, setChosenPosition] = useState<number | null>(null);
  const [expandedPlants, setExpandedPlants] = useState<Record<string, boolean>>({});
  const [activeDeponieArticleKey, setActiveDeponieArticleKey] = useState<string | null>(null);

  const searchMutation = useMutation<Article[], unknown, { search: string }>({
    mutationFn: ApiService.searchMaterials(),
  });

  const groupedResults = useMemo(() => {
    if (!searchMutation.data) {
      return [];
    }

    const groups: Record<string, Article[]> = {};

    for (const article of searchMutation.data) {
      const key = `${article.plantName} (${article.supplierName})`;

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push(article);
    }

    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [searchMutation.data]);

  const allOrders = supplierOrderQuery.data ?? [];

  const materialOrders = allOrders.filter((supplierOrder) => supplierOrder.number !== "DEPONIE");

  const deponieOrders = allOrders.filter((supplierOrder) => supplierOrder.number === "DEPONIE");

  const plantsByOrder = useMemo(() => {
    const map = new Map<
      number,
      {
        plantName: string;
        positions: OrderPosition[];
      }[]
    >();

    for (const supplierOrder of materialOrders) {
      const plantMap = new Map<
        string,
        {
          plantName: string;
          positions: OrderPosition[];
        }
      >();

      for (const position of supplierOrder.positions) {
        const plantName = position.article.plantName ?? "";

        if (!plantMap.has(plantName)) {
          plantMap.set(plantName, {
            plantName,
            positions: [position],
          });
        } else {
          plantMap.get(plantName)!.positions.push(position);
        }
      }

      map.set(supplierOrder.id, Array.from(plantMap.values()));
    }

    return map;
  }, [materialOrders]);

  const currentPlants = selectedOrderId !== null ? (plantsByOrder.get(selectedOrderId) ?? []) : [];

  const currentPlant =
    selectedPlantKey && currentPlants.length > 0 ? (currentPlants.find((plant) => plant.plantName === selectedPlantKey) ?? null) : null;

  const currentPlantPositions = useMemo(() => {
    if (!currentPlant) {
      return [];
    }

    const positionsByArticle: Record<string, OrderPosition[]> = {};

    for (const position of currentPlant.positions) {
      const key = position.article.number + "_" + position.article.name;

      if (!(key in positionsByArticle)) {
        positionsByArticle[key] = [];
      }

      positionsByArticle[key].push(position);
    }

    return Object.values(positionsByArticle).sort((a, b) => a[0].article.name.localeCompare(b[0].article.name));
  }, [currentPlant]);

  const articlesByOrder = useMemo(() => {
    const map = new Map<number, ArticleGroup[]>();

    for (const supplierOrder of deponieOrders) {
      const articleMap = new Map<
        string,
        {
          name: string;
          number: string;
          plantMap: Map<
            string,
            {
              key: string;
              name: string;
              distance: number;
              positions: OrderPosition[];
            }
          >;
        }
      >();

      for (const position of supplierOrder.positions) {
        const plantName = position.article.plantName ?? "";
        const plantKey = plantName;
        const plantDistance = position.article.plantDistance ?? 9999;

        const articleKey = position.article.number ?? position.article.name ?? String(position.id);

        const articleName = position.article.name ?? "";

        const articleNumber = position.article.number ?? "";

        if (!articleMap.has(articleKey)) {
          articleMap.set(articleKey, {
            name: articleName,
            number: articleNumber,
            plantMap: new Map(),
          });
        }

        const articleEntry = articleMap.get(articleKey)!;

        if (!articleEntry.plantMap.has(plantKey)) {
          articleEntry.plantMap.set(plantKey, {
            key: plantKey,
            name: plantName,
            distance: plantDistance,
            positions: [position],
          });
        } else {
          articleEntry.plantMap.get(plantKey)!.positions.push(position);
        }
      }

      const articleGroups: ArticleGroup[] = Array.from(articleMap.entries())
        .map(([key, article]) => ({
          key,
          name: article.name,
          number: article.number,
          plants: Array.from(article.plantMap.values()).sort((a, b) => a.distance - b.distance),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      map.set(supplierOrder.id, articleGroups);
    }

    return map;
  }, [deponieOrders]);

  const currentDeponieArticles = selectedOrderId !== null ? (articlesByOrder.get(selectedOrderId) ?? []) : [];

  const currentDeponieArticle =
    activeDeponieArticleKey && currentDeponieArticles.length > 0
      ? (currentDeponieArticles.find((article) => article.key === activeDeponieArticleKey) ?? null)
      : null;

  const handleSelectPosition = (position: OrderPosition) => {
    onSelect({
      position,
      article: position.article,
      amount: order.amount !== -1 ? 0 : order.amount,
      manualDAP: false,
      begleitschein: position.article.number.includes("*"),
    });
  };

  const handleBack = () => {
    if (view === "MATERIALS") {
      if (autoPlantSelected) {
        setView("ORDER");
        setSelectedOrderId(null);
        setSelectedPlantKey(null);
        setAutoPlantSelected(false);
      } else {
        setView("PLANTS");
        setSelectedPlantKey(null);
      }
    } else if (view === "PLANTS") {
      setView("ORDER");
      setSelectedOrderId(null);
      setSelectedPlantKey(null);
      setAutoPlantSelected(false);
    } else if (view === "MANUAL") {
      setView("ORDER");
      setChosenArticle(null);
    } else if (view === "DEPONIE_PLANTS") {
      setView("DEPONIE_ARTICLES");
      setActiveDeponieArticleKey(null);
    } else if (view === "DEPONIE_ARTICLES") {
      setView("ORDER");
      setSelectedOrderId(null);
      setActiveDeponieArticleKey(null);
    } else {
      onBack();
    }
  };

  const renderManualSearch = () => {
    const togglePlant = (plantName: string) => {
      setExpandedPlants((previous) => ({
        ...previous,
        [plantName]: !previous[plantName],
      }));
    };

    return (
      <Box sx={{ mt: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <ActionItem icon={ArrowBackIcon as any} label="zurück" color={undefined} onPress={handleBack} />

          <TextField
            fullWidth
            placeholder="Artikel suchen..."
            value={searchField}
            onChange={(event) => setSearchField(event.target.value)}
            inputMode="search"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                searchMutation.mutate({
                  search: searchField,
                });
              }
            }}
            sx={{
              alignSelf: "stretch",

              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "stretch",
              },

              "& .MuiOutlinedInput-input": {
                height: "100%",
                boxSizing: "border-box",
                paddingTop: "16.5px",
                paddingBottom: "16.5px",
              },
            }}
          />

          <Button
            variant="contained"
            onClick={() =>
              searchMutation.mutate({
                search: searchField,
              })
            }
            sx={{
              alignSelf: "stretch",
              minWidth: 66,
            }}
          >
            <SearchIcon />
          </Button>
        </Stack>

        {searchMutation.isIdle ? null : searchMutation.isError ? (
          <Typography align="center">Fehler bei der Suche</Typography>
        ) : searchMutation.isPending ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress />
          </Stack>
        ) : searchMutation.data.length === 0 ? (
          <Typography align="center">Nichts gefunden</Typography>
        ) : (
          groupedResults.map(([title, results]) => (
            <Box key={title} sx={{ mb: 1 }}>
              <Paper
                variant="outlined"
                onClick={() => togglePlant(title)}
                sx={{
                  display: "flex",
                  background: "#dbd9fdff",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Typography>{title}</Typography>

                {expandedPlants[title] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Paper>

              <Collapse in={!!expandedPlants[title]} timeout="auto" unmountOnExit>
                <Paper variant="outlined" sx={{ mt: 1 }}>
                  <List disablePadding>
                    {results.map((article, index) => {
                      const existingPositions = positionsByArticleId[article.id] ?? [];

                      const existingDeliveryTypeNames = new Set(
                        existingPositions.map((position) => normalizeDeliveryTypeName(position.deliveryType.name)),
                      );

                      const allUnpricedOptions = getUnpricedDeliveryOptions(article);

                      const unpricedOptions = isDispo
                        ? allUnpricedOptions.filter((option) => !existingDeliveryTypeNames.has(normalizeDeliveryTypeName(option.name)))
                        : existingPositions.length === 0
                          ? allUnpricedOptions
                          : [];

                      return (
                        <Box key={article.id}>
                          <ListItem disablePadding>
                            <ListItemButton
                              onClick={() => setChosenArticle((current) => (current === article.id ? null : article.id))}
                              sx={{ py: 0 }}
                            >
                              <ListItemText primary={article.name} secondary={article.number} />

                              {chosenArticle === article.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>
                          </ListItem>

                          <Collapse in={chosenArticle === article.id} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                pl: 0,
                                pb: 0,
                                backgroundColor: "#f0f0f0",
                              }}
                            >
                              <List disablePadding>
                                <Divider />

                                {existingPositions.map((position, positionIndex) => (
                                  <Fragment key={position.id}>
                                    <ListItem disablePadding>
                                      <ListItemButton
                                        onClick={() =>
                                          onSelect({
                                            position,
                                            amount: 0,
                                            begleitschein: position.article.number.includes("*"),
                                            article: position.article,
                                            manualDAP: false,
                                          })
                                        }
                                        sx={{ py: 0 }}
                                      >
                                        <ArrowRight
                                          sx={{
                                            mr: 1,
                                          }}
                                        />

                                        <ListItemText primary={position.deliveryType.name} />

                                        <ChevronRightIcon />
                                      </ListItemButton>
                                    </ListItem>

                                    {positionIndex < existingPositions.length - 1 || unpricedOptions.length > 0 ? <Divider /> : null}
                                  </Fragment>
                                ))}

                                {unpricedOptions.map((option, optionIndex) => (
                                  <Fragment key={`${article.id}-${option.name}`}>
                                    <ListItem disablePadding>
                                      <ListItemButton
                                        onClick={() =>
                                          onSelect({
                                            position: null,
                                            article,
                                            amount: 0,
                                            manualDAP: option.manualDAP,
                                          })
                                        }
                                        sx={{ py: 0 }}
                                      >
                                        <ArrowRight
                                          sx={{
                                            mr: 1,
                                          }}
                                        />

                                        <ListItemText primary={option.name} />

                                        {isDispo ? (
                                          <WarningAmberIcon
                                            color="warning"
                                            fontSize="small"
                                            titleAccess="Unbepreiste Zufuhrart"
                                            sx={{
                                              mr: 1,
                                            }}
                                          />
                                        ) : null}

                                        <ChevronRightIcon />
                                      </ListItemButton>
                                    </ListItem>

                                    {optionIndex < unpricedOptions.length - 1 ? <Divider /> : null}
                                  </Fragment>
                                ))}
                              </List>
                            </Box>
                          </Collapse>

                          {index < results.length - 1 ? <Divider /> : null}
                        </Box>
                      );
                    })}
                  </List>
                </Paper>
              </Collapse>
            </Box>
          ))
        )}
      </Box>
    );
  };

  if (supplierOrderQuery.isLoading) {
    return <CircularProgress />;
  }

  if (supplierOrderQuery.isError) {
    return <Typography>Fehler beim Laden der Lieferantenaufträge</Typography>;
  }

  return (
    <Fragment>
      {view === "ORDER" && (
        <Box>
          <Stack direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Typography variant="button">Lieferant wählen</Typography>
          </Stack>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <ActionItem icon={ArrowBackIcon as any} label="zurück" color={undefined} onPress={handleBack} />

            {materialOrders.map((supplierOrder) => (
              <ActionItem
                key={supplierOrder.id}
                icon={DescriptionIcon as any}
                big
                label={supplierOrder.supplier.name}
                sublabel={supplierOrder.number}
                color="blue"
                onPress={() => {
                  setSelectedOrderId(supplierOrder.id);

                  const plants = plantsByOrder.get(supplierOrder.id) ?? [];

                  if (plants.length === 1) {
                    setSelectedPlantKey(plants[0].plantName);
                    setAutoPlantSelected(true);
                    setView("MATERIALS");
                  } else {
                    setAutoPlantSelected(false);
                    setView("PLANTS");
                  }
                }}
              />
            ))}

            {deponieOrders.map((supplierOrder) => (
              <ActionItem
                key={supplierOrder.id}
                icon={RecyclingIcon as any}
                big
                label="Deponie"
                sublabel="Materialentsorgung"
                color="orange"
                onPress={() => {
                  setSelectedOrderId(supplierOrder.id);
                  setActiveDeponieArticleKey(null);
                  setView("DEPONIE_ARTICLES");
                }}
              />
            ))}

            <ActionItem
              icon={SearchIcon as any}
              big
              label="Manuelle Suche"
              sublabel="Artikel über Suche wählen"
              color="pink"
              onPress={() => setView("MANUAL")}
            />
          </Box>
        </Box>
      )}

      {view === "PLANTS" && selectedOrderId !== null && (
        <Box>
          <Stack direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Typography variant="button">Lieferwerk wählen</Typography>
          </Stack>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <ActionItem icon={ArrowBackIcon as any} label="zurück" color={undefined} onPress={handleBack} />

            {currentPlants.map((plant) => (
              <ActionItem
                key={plant.plantName || "plant"}
                icon={FactoryIcon as any}
                big
                label={plant.plantName || "Unbekannte Anlage"}
                color={undefined}
                onPress={() => {
                  setSelectedPlantKey(plant.plantName);
                  setAutoPlantSelected(false);
                  setView("MATERIALS");
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {view === "MANUAL" && (
        <Box>
          <Stack direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Typography variant="button">Material suchen</Typography>
          </Stack>

          {renderManualSearch()}
        </Box>
      )}

      {view === "MATERIALS" && (
        <Box>
          <Stack direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Typography variant="button">{currentPlant ? currentPlant.plantName || "Unbekanntes Werk" : "Unbekanntes Werk"}</Typography>
          </Stack>

          <Box>
            <Box
              sx={{
                mb: 2,
                display: "flex",
              }}
            >
              <ActionItem icon={ArrowBackIcon as any} label="zurück" color={undefined} onPress={handleBack} />
            </Box>

            {!currentPlant ? (
              <Typography>Keine Anlage für diesen Auftrag gefunden. Bitte zurück und neu wählen.</Typography>
            ) : currentPlant.positions.length === 0 ? (
              <Typography>Keine Materialien für diese Anlage.</Typography>
            ) : (
              <Paper variant="outlined">
                <List disablePadding>
                  {currentPlantPositions.map((positions, index) => (
                    <Box key={positions[0]?.id ?? index}>
                      {positions.length > 1 ? (
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => setChosenPosition((current) => (current === positions[0].id ? null : positions[0].id))}
                            sx={{ py: 0 }}
                          >
                            <ListItemText
                              primary={positions[0].article.name}
                              secondary={
                                `${!!positions[0].orderPrice && positions[0].orderPrice > 0 ? `${formatEuro(positions[0].orderPrice)} - ` : ""}` +
                                positions[0].article.number
                              }
                            />

                            {chosenPosition === positions[0].id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </ListItemButton>
                        </ListItem>
                      ) : (
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => handleSelectPosition(positions[0])} sx={{ py: 0 }}>
                            <ListItemText
                              primary={positions[0].article.name}
                              secondary={
                                `${!!positions[0].orderPrice && positions[0].orderPrice > 0 ? `${formatEuro(positions[0].orderPrice)} - ` : ""}` +
                                `${positions[0].article.number} - ${positions[0].deliveryType.name} - ${positions[0].amount} ${positions[0].article.unit}`
                              }
                            />

                            <ChevronRightIcon />
                          </ListItemButton>
                        </ListItem>
                      )}

                      {positions.length > 1 ? (
                        <Collapse in={chosenPosition === positions[0].id} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              pl: 0,
                              pb: 0,
                              backgroundColor: "#f0f0f0",
                            }}
                          >
                            <List disablePadding>
                              <Divider />

                              {positions.map((position, positionIndex) => (
                                <Fragment key={position.id}>
                                  <ListItem disablePadding>
                                    <ListItemButton onClick={() => handleSelectPosition(position)} sx={{ py: 0 }}>
                                      <ArrowRight
                                        sx={{
                                          mr: 1,
                                        }}
                                      />

                                      <ListItemText primary={position.deliveryType.name} secondary={`${position.amount} ${position.article.unit}`} />

                                      <ChevronRightIcon />
                                    </ListItemButton>
                                  </ListItem>

                                  {positionIndex < positions.length - 1 ? <Divider /> : null}
                                </Fragment>
                              ))}
                            </List>
                          </Box>
                        </Collapse>
                      ) : null}

                      {index < currentPlantPositions.length - 1 ? <Divider /> : null}
                    </Box>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </Box>
      )}

      {view === "DEPONIE_ARTICLES" && (
        <Box>
          <Stack direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Typography variant="button">Deponie-Artikel wählen</Typography>
          </Stack>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <ActionItem icon={ArrowBackIcon as any} label="zurück" color={undefined} onPress={handleBack} />

            {currentDeponieArticles.map((article) => (
              <ActionItem
                key={article.key}
                icon={TerrainIcon as any}
                label={article.name || "Unbekannter Artikel"}
                sublabel={article.number || "Ohne Nummer"}
                color={undefined}
                onPress={() => {
                  setActiveDeponieArticleKey(article.key);
                  setView("DEPONIE_PLANTS");
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {view === "DEPONIE_PLANTS" && (
        <Box>
          <Stack direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Typography variant="button">
              {currentDeponieArticle?.name} ({currentDeponieArticle?.number})
            </Typography>
          </Stack>

          {!currentDeponieArticle || currentDeponieArticle.plants.length === 0 ? (
            <Typography>Keine Werke für diesen Artikel gefunden</Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <ActionItem icon={ArrowBackIcon as any} label="zurück" color={undefined} onPress={handleBack} />

              {currentDeponieArticle.plants.map((plant) => {
                const position = plant.positions[0] ?? null;

                const subTitle = [formatDistance(plant.positions[0]?.article.plantDistance ?? "")].filter(Boolean).join(" - ");

                return (
                  <ActionItem
                    key={plant.key}
                    icon={FactoryIcon as any}
                    label={plant.name}
                    sublabel={subTitle || undefined}
                    color={undefined}
                    onPress={() => {
                      if (position) {
                        handleSelectPosition(position);
                      }
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Fragment>
  );
}
